import { FieldValidator, useField, useFormikContext } from 'formik';
import { forwardRef, useMemo } from 'react';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';

import { composeFieldValidators, FROM_STRIPE_CENTS, TO_STRIPE_CENTS } from '@jebel/utils';

import { TextField, TextFieldProps } from 'shared/components/ui/TextField';
import { required } from 'shared/utils/form';

import { getIsInvalid } from '../ui';

export interface FormMoneyCentsProps
  extends Omit<NumberFormatProps<Omit<TextFieldProps, 'inputProps'>>, 'prefix'> {
  /** Name of the field. */
  name: string;

  validate?: FieldValidator;
}

export const FormMoneyCents = forwardRef<HTMLInputElement, FormMoneyCentsProps>(
  ({ name, required: isRequired, validate, ...props }, ref) => {
    const form = useFormikContext();

    const validation = composeFieldValidators(isRequired ? required : undefined, validate);

    const [field, meta, helpers] = useField<number>({
      name: name as string,
      validate: validation,
    });

    const isInvalid = useMemo(() => {
      return getIsInvalid({ meta, form });
    }, [meta, form]);

    const errorText = useMemo(() => {
      return isInvalid && meta.error;
    }, [isInvalid, meta.error]);

    const value = useMemo(() => {
      if (field.value === 0) {
        // Prevent to transform 0 to $1.00
        return 0;
      }

      return Number.isSafeInteger(field.value) ? field.value * FROM_STRIPE_CENTS : '';
    }, [field.value]);

    const onChange = (values: NumberFormatValues) => {
      if (Number.isSafeInteger(values.floatValue)) {
        const dollars = values.floatValue as number;
        helpers.setValue(dollars * TO_STRIPE_CENTS);
      }
    };

    return (
      <NumberFormat<TextFieldProps>
        variant="outlined"
        {...props}
        prefix="$"
        placeholder="$0.00"
        inputRef={ref}
        helperText={errorText}
        error={isInvalid}
        name={name}
        value={value}
        required={isRequired}
        customInput={TextField}
        decimalScale={2}
        allowLeadingZeros
        fixedDecimalScale
        onValueChange={onChange}
      />
    );
  },
);
